// src/pages/Admin/Users/index.tsx
import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  UserCog,
  Mail,
  Phone,
  Calendar,
  Shield,
  CheckCircle,
  XCircle,
  Users as UsersIcon,
  Eye,
  Ban,
  Check
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { userAPI } from '../../../api/user';
import { hasAnyRole } from '../../../utils/roleUtils';
import type { User, PaginatedResponse } from '../../../types';
import RoleAssignmentModal from './RoleAssignmentModal';
import UserDetailModal from './UserDetailModal';

const UsersManagementPage: React.FC = () => {
  const { user: currentUser, hasRole } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    pages: 1
  });

  const roles = ['all', 'customer', 'driver', 'warehouse', 'staff', 'manager', 'admin'];
  const statuses = ['all', 'active', 'inactive'];

  const canManageRoles = hasRole('admin');
  const canViewAllUsers = hasAnyRole(currentUser?.roles || [], ['admin', 'manager', 'staff']);

  useEffect(() => {
    if (canViewAllUsers) {
      fetchUsers();
    }
  }, [searchQuery, selectedRole, selectedStatus, pagination.page]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // This is a placeholder - you'll need to create this admin endpoint
      // For now, we'll simulate with a custom params object
      const params: any = {
        page: pagination.page,
        page_size: 12,
      };
      
      if (searchQuery) params.search = searchQuery;
      if (selectedRole !== 'all') params.role = selectedRole;
      if (selectedStatus !== 'all') params.is_active = selectedStatus === 'active';

      // Mock response for now - replace with actual API call
      // const response = await adminAPI.getUsers(params);
      
      // Simulated response
      const mockResponse = {
        data: {
          results: [] as User[],
          count: 0,
          next: null,
          previous: null
        }
      };

      // For demo purposes, you can uncomment this to test with mock data
      // setUsers(mockResponse.data.results);
      // setPagination({
      //   page: pagination.page,
      //   total: mockResponse.data.count,
      //   pages: Math.ceil(mockResponse.data.count / 12)
      // });
      
      setUsers([]); // Remove this when actual API is implemented
      setPagination({ page: 1, total: 0, pages: 1 });
      
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleUpdate = async (userId: number, newRoles: string[]) => {
    try {
      // Handle role changes
      const currentRoles = selectedUser?.roles || [];
      
      // Roles to add
      const rolesToAdd = newRoles.filter(role => !currentRoles.includes(role));
      // Roles to remove
      const rolesToRemove = currentRoles.filter(role => !newRoles.includes(role));

      // Process role additions
      for (const role of rolesToAdd) {
        await userAPI.assignRole(userId, role);
      }

      // Process role removals
      for (const role of rolesToRemove) {
        await userAPI.removeRole(userId, role);
      }

      // Refresh users list
      fetchUsers();
      setShowRoleModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Failed to update user role:', error);
    }
  };

  const handleToggleUserStatus = async (userId: number, currentStatus: boolean) => {
    try {
      // Toggle user active status
      // await adminAPI.updateUser(userId, { is_active: !currentStatus });
      console.log('Toggle user status:', userId, !currentStatus);
      fetchUsers();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      manager: 'bg-purple-100 text-purple-800',
      staff: 'bg-blue-100 text-blue-800',
      driver: 'bg-green-100 text-green-800',
      warehouse: 'bg-orange-100 text-orange-800',
      customer: 'bg-gray-100 text-gray-800'
    };
    return colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getVerificationBadges = (user: User) => {
    const badges = [];
    if (user.email_verified) badges.push({ label: 'Email', color: 'bg-green-100 text-green-800' });
    if (user.phone_verified) badges.push({ label: 'Phone', color: 'bg-green-100 text-green-800' });
    if (user.is_id_verified) badges.push({ label: 'ID', color: 'bg-green-100 text-green-800' });
    if (user.id_verification_status === 'pending') badges.push({ label: 'ID Pending', color: 'bg-yellow-100 text-yellow-800' });
    if (user.id_verification_status === 'rejected') badges.push({ label: 'ID Rejected', color: 'bg-red-100 text-red-800' });
    return badges;
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setShowDetailModal(true);
  };

  if (!canViewAllUsers) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            You don't have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
        <div className="text-sm text-gray-500">
          Total Users: {pagination.total}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by name, email, phone..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Filter size={18} className="text-gray-400" />
              <select
                value={selectedRole}
                onChange={(e) => {
                  setSelectedRole(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {roles.map(role => (
                  <option key={role} value={role}>
                    {role === 'all' ? 'All Roles' : role.charAt(0).toUpperCase() + role.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          
          <button
            onClick={fetchUsers}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : users.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filter.
            </p>
          </div>
        ) : (
          users.map((user) => (
            <div key={user.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-lg font-medium text-blue-600">
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">
                      {user.full_name || `${user.first_name} ${user.last_name}`}
                    </h3>
                    <div className="flex items-center mt-1">
                      <Mail size={14} className="text-gray-400 mr-1" />
                      <p className="text-xs text-gray-500 truncate max-w-[150px]">{user.email}</p>
                      {user.email_verified && (
                        <CheckCircle size={12} className="ml-1 text-green-500" />
                      )}
                    </div>
                    {user.phone && (
                      <div className="flex items-center mt-1">
                        <Phone size={14} className="text-gray-400 mr-1" />
                        <p className="text-xs text-gray-500">{user.phone}</p>
                        {user.phone_verified && (
                          <CheckCircle size={12} className="ml-1 text-green-500" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => handleViewUser(user)}
                    className="p-1.5 hover:bg-gray-100 rounded-lg"
                    title="View Details"
                  >
                    <Eye size={16} className="text-gray-600" />
                  </button>
                  {canManageRoles && (
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setShowRoleModal(true);
                      }}
                      className="p-1.5 hover:bg-gray-100 rounded-lg"
                      title="Manage Roles"
                    >
                      <UserCog size={16} className="text-gray-600" />
                    </button>
                  )}
                </div>
              </div>

              {/* Roles */}
              <div className="mt-4 flex flex-wrap gap-2">
                {user.roles?.map((role: string) => (
                  <span
                    key={role}
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(role)}`}
                  >
                    {role}
                  </span>
                ))}
              </div>

              {/* Verification Badges */}
              <div className="mt-3 flex flex-wrap gap-2">
                {getVerificationBadges(user).map((badge, index) => (
                  <span
                    key={index}
                    className={`px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}
                  >
                    {badge.label} ✓
                  </span>
                ))}
              </div>

              {/* Account Status */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-xs text-gray-500">
                    <Calendar size={14} className="mr-1" />
                    Joined {new Date(user.created_at).toLocaleDateString()}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {canManageRoles && (
                      <button
                        onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                        className={`p-1 rounded-lg ${
                          user.is_active 
                            ? 'text-red-600 hover:bg-red-50' 
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={user.is_active ? 'Deactivate User' : 'Activate User'}
                      >
                        {user.is_active ? <Ban size={14} /> : <Check size={14} />}
                      </button>
                    )}
                  </div>
                </div>
                {user.last_login && (
                  <p className="text-xs text-gray-400 mt-1">
                    Last login: {new Date(user.last_login).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {((pagination.page - 1) * 12) + 1} to {Math.min(pagination.page * 12, pagination.total)} of {pagination.total} users
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
              let pageNum;
              if (pagination.pages <= 5) {
                pageNum = i + 1;
              } else if (pagination.page <= 3) {
                pageNum = i + 1;
              } else if (pagination.page >= pagination.pages - 2) {
                pageNum = pagination.pages - 4 + i;
              } else {
                pageNum = pagination.page - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-1 rounded-md text-sm ${
                    pagination.page === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showRoleModal && selectedUser && (
        <RoleAssignmentModal
          user={selectedUser}
          currentUserRole={currentUser?.primaryRole || ''}
          onAssign={(roles) => handleRoleUpdate(selectedUser.id, roles)}
          onClose={() => {
            setShowRoleModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {showDetailModal && selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedUser(null);
          }}
          onRoleEdit={() => {
            setShowDetailModal(false);
            setShowRoleModal(true);
          }}
        />
      )}
    </div>
  );
};

export default UsersManagementPage;